import { ChevronDown } from 'lucide-react';
import * as React from 'react';

import { cn } from '@/lib/utils';

import {
  splitButtonContainerVariants,
  splitButtonPrimaryVariants,
  splitButtonDropdownVariants,
} from './SplitButton.styles';

import type { SplitButtonProps } from './SplitButton.types';

/**
 * Split Button component
 *
 * Button with a primary action and a dropdown menu trigger.
 * The primary button executes the main action, while the dropdown
 * toggle reveals additional options.
 * Based on Figma Design System Nabat 2.0 - Split Button
 *
 * @example
 * // Basic split button
 * <SplitButton
 *   onPrimaryClick={() => console.log('Save')}
 *   onDropdownClick={() => console.log('Open menu')}
 * >
 *   Save
 * </SplitButton>
 *
 * @example
 * // With icon
 * <SplitButton
 *   iconStart={<Save />}
 *   kind="brand"
 *   onPrimaryClick={handleSave}
 *   onDropdownClick={toggleMenu}
 * >
 *   Save
 * </SplitButton>
 */
export const SplitButton = React.forwardRef<HTMLDivElement, SplitButtonProps>(
  (
    {
      className,
      appearance = 'solid',
      kind = 'neutral',
      scale = 'm',
      width,
      children,
      iconStart,
      onPrimaryClick,
      onDropdownClick,
      primaryDisabled = false,
      dropdownDisabled = false,
      dropdownOpen = false,
      ...props
    },
    ref
  ) => {
    let chevronSize = 16;
    if (scale === 'l') chevronSize = 20;
    else if (scale === 's') chevronSize = 12;
    return (
      <div ref={ref} className={cn(splitButtonContainerVariants({ width, className }))} {...props}>
        {/* Primary action button */}
        <button
          type="button"
          className={cn(splitButtonPrimaryVariants({ appearance, kind, scale }))}
          onClick={onPrimaryClick}
          disabled={primaryDisabled}
        >
          {iconStart && <span className="shrink-0">{iconStart}</span>}
          <span className="whitespace-nowrap">{children}</span>
        </button>

        {/* Dropdown trigger button */}
        <button
          type="button"
          className={cn(splitButtonDropdownVariants({ appearance, kind, scale }))}
          onClick={onDropdownClick}
          disabled={dropdownDisabled}
          aria-label="Show more options"
          aria-expanded={dropdownOpen}
          aria-haspopup="true"
        >
          <ChevronDown size={chevronSize} />
        </button>
      </div>
    );
  }
);

SplitButton.displayName = 'SplitButton';
